import React, { useState, FC } from "react";
import { useParams } from "react-router-dom";

import {
  useRightSidebarState,
  useApisMembersKnowledgeInsightPostsNew,
  useSearchMembersKnowledgeInsightPosts,
  useApisMembersKnowledgeInsightPostsKnowledgeInsightPostCommentsIndex,
  useApiPolling,
} from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";
import {
  KnowledgeInsightPostDetailWithEditForm,
  KnowledgeInsightPostCommentsWithKnowledgeInsightPost,
  KnowledgeInsightPostSearchForm,
} from "~/components/organisms";

import { AllRoles, ManagerRole } from "~/domains";

import {
  RightSidebarToggleIconWithLabel,
  InfiniteScroll,
  FilterIconWithSortMenu,
} from "~/ui";

export const MembersKnowledgeInsightPostsFromManagersIndex: FC = () => {
  const { employeeRole } = useParams<{
    employeeRole: string;
  }>();
  const role =
    AllRoles.find((role) => role.type === employeeRole) || ManagerRole;

  const { data: fetchNewData } = useApisMembersKnowledgeInsightPostsNew();

  const {
    isLoading,
    knowledgeInsightPosts,
    startDate,
    endDate,
    selectableDropdownHasBookmarkIds,
    selectedDropdownHasBookmarkId,
    selectableDropdownHasCommentIds,
    selectedDropdownHasCommentId,
    hasNextPage,
    sort,
    selectableDropdownEmployees,
    selectedDropdownEmployees,
    keyword,
    deleteIndexKnowledgeInsightPost,
    updateIndexKnowledgeInsightPost,
    onChangeDropdownEmployees,
    onChangeDateRange,
    onChangeKeyword,
    fetchNextPage,
    setSort,
    onChangeDropdownHasBookmarkId,
    onChangeDropdownHasCommentId,
    onSearchSubmit,
    onConditionReset,
  } = useSearchMembersKnowledgeInsightPosts({
    employeeRoleId: role.id,
  });
  const [
    selectGetCommentKnowledgeInsightPostId,
    setSelectGetCommentKnowledgeInsightPostId,
  ] = useState<string>("");
  const {
    data: knowledgeInsightPostCommentsIndexData,
    refetch: commentsRefetch,
  } = useApisMembersKnowledgeInsightPostsKnowledgeInsightPostCommentsIndex({
    knowledgeInsightPostId: selectGetCommentKnowledgeInsightPostId,
    config: {
      enabled: Boolean(selectGetCommentKnowledgeInsightPostId),
    },
  });

  const {
    isSearchOpen,
    isReplyOpen,
    searchOpenToggle,
    setReplyOpen,
    setClose,
    isOpen,
  } = useRightSidebarState("isClose");

  useApiPolling({
    enabled: isReplyOpen && Boolean(selectGetCommentKnowledgeInsightPostId),
    interval: 1000,
    pollingFunction: commentsRefetch,
    timeoutDuration: null,
  });

  const openComment = (knowledgeInsightPostId: string) => {
    setSelectGetCommentKnowledgeInsightPostId(knowledgeInsightPostId);
    setReplyOpen();
  };

  const breadCrumbItems = [
    {
      label: role === ManagerRole ? "部署の発信" : "本部の発信",
      href: `/knowledge_post/${employeeRole}`,
    },
  ];

  return (
    <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar>
        <FilterIconWithSortMenu
          likeSort={() => setSort("likeCountDesc")}
          bookmarkSort={() => setSort("bookmarkCountDesc")}
          createdAtAscSort={() => setSort("createdAtAsc")}
          createdAtDescSort={() => setSort("createdAtDesc")}
          currentSortType={sort}
        />
        <InfiniteScroll
          itemsLength={knowledgeInsightPosts.length}
          nextFetchFunction={fetchNextPage}
          hasMore={hasNextPage}
          isLoading={isLoading}
          className="border-t-2 border-secondary-200"
          noContentMessage="ナレッジシェアがありません"
        >
          {knowledgeInsightPosts.map((post, index) => (
            <KnowledgeInsightPostDetailWithEditForm
              key={post.id}
              knowledgeInsightPost={post}
              openComment={openComment}
              deleteItem={() => {
                deleteIndexKnowledgeInsightPost(index);
                if (selectGetCommentKnowledgeInsightPostId === post.id) {
                  setClose();
                  setSelectGetCommentKnowledgeInsightPostId("");
                }
              }}
              updateItem={(newItem) =>
                updateIndexKnowledgeInsightPost(newItem, index)
              }
            />
          ))}
        </InfiniteScroll>
      </EmployeeLayoutMainContent>
      <EmployeeLayoutRightSidebar>
        <RightSidebarToggleIconWithLabel
          iconOnClick={searchOpenToggle}
          handleOnClose={setClose}
          label={isSearchOpen ? "検索条件" : "リプライ"}
          isOpen={isOpen}
        >
          {isSearchOpen && (
            <KnowledgeInsightPostSearchForm
              onSubmit={onSearchSubmit}
              onConditionReset={onConditionReset}
              date={{
                startDate: startDate,
                endDate: endDate,
                onChange: onChangeDateRange,
              }}
              employee={{
                options: selectableDropdownEmployees,
                value: selectedDropdownEmployees,
                onChange: onChangeDropdownEmployees,
              }}
              hasComment={{
                options: selectableDropdownHasCommentIds,
                value: selectedDropdownHasCommentId,
                onChange: onChangeDropdownHasCommentId,
              }}
              hasBookmark={{
                options: selectableDropdownHasBookmarkIds,
                value: selectedDropdownHasBookmarkId,
                onChange: onChangeDropdownHasBookmarkId,
              }}
              searchKeyword={{
                value: keyword,
                onChange: onChangeKeyword,
              }}
            />
          )}
          {isReplyOpen && knowledgeInsightPostCommentsIndexData && (
            <KnowledgeInsightPostCommentsWithKnowledgeInsightPost
              className="mt-6"
              refetch={commentsRefetch}
              mentions={fetchNewData?.mentionableEmployees || []}
              knowledgeInsightPost={
                knowledgeInsightPostCommentsIndexData.knowledgeInsightPost
              }
              knowledgeInsightPostComments={
                knowledgeInsightPostCommentsIndexData.knowledgeInsightPostComments
              }
            />
          )}
        </RightSidebarToggleIconWithLabel>
      </EmployeeLayoutRightSidebar>
    </EmployeeLayout>
  );
};
